import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  getAgentAgreement,
  getDocumentRenderHtml,
  getIssuedDocumentDownload,
  getMyAgencyAgreement,
  saveDocument,
} from '@/lib/api';
import {
  collectDocumentFields,
  mirrorDocumentField,
  selectDocumentFields,
  setDocumentEditHighlights,
} from '@/lib/documentEditor';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertCircle,
  FileDown,
  FileText,
  Highlighter,
  History,
  Loader2,
  Pencil,
  RefreshCw,
  Save,
} from 'lucide-react';

function safeList(value) {
  return Array.isArray(value) ? value : [];
}

function cleanError(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object') return detail.message || fallback;
  return err?.response?.data?.error || fallback;
}

function formatDateTime(value) {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleString();
}

function labelize(value) {
  return String(value || '')
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function documentStatus(document) {
  return labelize(document?.status || 'draft') || 'Draft';
}

function statusClass(status) {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'issued' || normalized === 'active') {
    return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  }
  if (normalized === 'revoked') {
    return 'border-rose-200 bg-rose-50 text-rose-700';
  }
  return 'border-amber-200 bg-amber-50 text-amber-700';
}

function documentTimestamp(document) {
  return formatDateTime(
    document?.updated_at || document?.issued_at || document?.created_at,
  );
}

export default function AgencyAgreementCard({
  mode = 'self',
  agentId,
  className,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [downloadingId, setDownloadingId] = useState('');
  const [editor, setEditor] = useState(null);
  const [editorLoading, setEditorLoading] = useState(false);
  const [editorSaving, setEditorSaving] = useState(false);
  const [showHighlights, setShowHighlights] = useState(true);
  const requestSequence = useRef(0);
  const editorRequestSequence = useRef(0);
  const frameRef = useRef(null);
  const frameCleanupRef = useRef(null);
  const dirtyFieldsRef = useRef(new Set());

  const canLoad = mode === 'self' || Boolean(agentId);

  const loadAgreement = useCallback(async () => {
    if (!canLoad) {
      setData(null);
      setLoading(false);
      setError('Agency details are required to load the agreement.');
      return;
    }

    const sequence = ++requestSequence.current;
    setLoading(true);
    setError('');
    try {
      const response = mode === 'admin'
        ? await getAgentAgreement(agentId)
        : await getMyAgencyAgreement();
      if (sequence === requestSequence.current) setData(response.data || null);
    } catch (err) {
      console.error(err);
      if (sequence === requestSequence.current) {
        setData(null);
        setError(cleanError(err, 'The agency agreement could not be loaded.'));
      }
    } finally {
      if (sequence === requestSequence.current) setLoading(false);
    }
  }, [agentId, canLoad, mode]);

  useEffect(() => {
    setHistoryOpen(false);
    loadAgreement();
  }, [loadAgreement]);

  useEffect(() => () => {
    requestSequence.current += 1;
    editorRequestSequence.current += 1;
    frameCleanupRef.current?.();
  }, []);

  const agreement = data?.agreement || null;
  const history = safeList(data?.history).filter(
    (document) => document?.id && document.id !== agreement?.id,
  );
  const title = data?.label || 'Student Recruitment Agreement';
  const variant = labelize(data?.variant) || labelize(data?.agency_type);

  const handleDownload = async (document) => {
    if (!document?.id) return;
    try {
      setDownloadingId(document.id);
      const response = await getIssuedDocumentDownload(document.id);
      if (response.data?.url) {
        window.open(response.data.url, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      console.error(err);
      toast.error(cleanError(err, 'The agreement download could not be prepared.'));
    } finally {
      setDownloadingId('');
    }
  };

  const openEditor = async () => {
    if (!agreement?.id) return;
    const sequence = ++editorRequestSequence.current;
    frameCleanupRef.current?.();
    dirtyFieldsRef.current = new Set();
    setShowHighlights(true);
    setEditorLoading(true);
    setEditor({ documentId: agreement.id, html: '', label: title });
    try {
      const response = await getDocumentRenderHtml(agreement.id);
      if (sequence === editorRequestSequence.current) {
        setEditor({
          documentId: agreement.id,
          html: response.data?.html || '',
          label: response.data?.document?.file_name || title,
        });
      }
    } catch (err) {
      console.error(err);
      if (sequence === editorRequestSequence.current) {
        toast.error(cleanError(err, 'The agreement editor could not be loaded.'));
        setEditor(null);
      }
    } finally {
      if (sequence === editorRequestSequence.current) setEditorLoading(false);
    }
  };

  const closeEditor = () => {
    if (editorSaving) return;
    editorRequestSequence.current += 1;
    frameCleanupRef.current?.();
    frameCleanupRef.current = null;
    setEditorLoading(false);
    setEditor(null);
  };

  const handleFrameLoad = () => {
    frameCleanupRef.current?.();
    const frame = frameRef.current;
    const frameDocument = frame?.contentDocument;
    const root = frameDocument?.querySelector('[data-document-editor]');
    if (!frame || !frameDocument || !root) return;

    setDocumentEditHighlights(frameDocument, showHighlights);

    const syncHeight = () => {
      const nextHeight = Math.max(
        720,
        frameDocument.documentElement?.scrollHeight || 0,
        frameDocument.body?.scrollHeight || 0,
      );
      frame.style.height = `${nextHeight + 24}px`;
    };
    const scheduleHeight = () => {
      const frameWindow = frame.contentWindow;
      if (frameWindow?.requestAnimationFrame) frameWindow.requestAnimationFrame(syncHeight);
      else syncHeight();
    };
    const handleInput = (event) => {
      const field = event.target?.getAttribute?.('data-field');
      if (field) dirtyFieldsRef.current.add(field);
      mirrorDocumentField(frameDocument, event.target);
      scheduleHeight();
    };
    const handleChange = (event) => {
      const field = event.target?.getAttribute?.('data-toggle');
      if (field) dirtyFieldsRef.current.add(field);
      scheduleHeight();
    };

    root.addEventListener('input', handleInput);
    root.addEventListener('change', handleChange);
    const ResizeObserverClass = frame.contentWindow?.ResizeObserver;
    const resizeObserver = ResizeObserverClass ? new ResizeObserverClass(scheduleHeight) : null;
    if (resizeObserver && frameDocument.body) resizeObserver.observe(frameDocument.body);

    scheduleHeight();
    frameDocument.fonts?.ready?.then(scheduleHeight).catch(() => {});
    frameCleanupRef.current = () => {
      root.removeEventListener('input', handleInput);
      root.removeEventListener('change', handleChange);
      resizeObserver?.disconnect();
    };
  };

  const toggleHighlights = () => {
    setShowHighlights((current) => {
      const next = !current;
      setDocumentEditHighlights(frameRef.current?.contentDocument, next);
      return next;
    });
  };

  const handleSave = async () => {
    const frameDocument = frameRef.current?.contentDocument;
    if (!editor?.documentId || !frameDocument) return;
    const fields = selectDocumentFields(
      collectDocumentFields(frameDocument),
      dirtyFieldsRef.current,
    );
    if (Object.keys(fields).length === 0) {
      toast.info('No agreement changes to save.');
      return;
    }
    try {
      setEditorSaving(true);
      await saveDocument(editor.documentId, fields);
      toast.success('Agreement saved as a new version.');
      frameCleanupRef.current?.();
      frameCleanupRef.current = null;
      setEditor(null);
      await loadAgreement();
    } catch (err) {
      console.error(err);
      toast.error(cleanError(err, 'The agreement could not be saved.'));
    } finally {
      setEditorSaving(false);
    }
  };

  return (
    <Card className={cn('border border-[rgba(6,43,26,0.08)] shadow-sm', className)}>
      <CardHeader className="pb-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle
              className="flex items-center gap-2 text-base font-semibold text-[#062B1A]"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              <FileText className="h-4 w-4 text-[#D4AF37]" />
              Student Recruitment Agreement
            </CardTitle>
            <p className="mt-1 text-xs text-[#062B1A]/50">
              Automatically prepared from the agency profile and editable at any time.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={loadAgreement}
            disabled={loading || !canLoad}
          >
            <RefreshCw className={cn('mr-2 h-4 w-4', loading && 'animate-spin')} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-[#062B1A]/55">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading agreement...
          </div>
        ) : error ? (
          <div className="flex items-start gap-3 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        ) : data?.available === false ? (
          <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-700" />
            <div>
              <p className="text-sm font-semibold text-amber-900">Agency type required</p>
              <p className="mt-1 text-sm text-amber-800/80">
                {mode === 'admin'
                  ? 'Set the agency type to Domestic, International, or Personal to prepare the matching agreement.'
                  : 'An administrator needs to set the agency type before the matching agreement can be prepared.'}
              </p>
            </div>
          </div>
        ) : !agreement ? (
          <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>The agreement is not ready yet. Refresh to try preparing it again.</span>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-lg border border-[rgba(6,43,26,0.08)] bg-white/70 p-4">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium text-[#062B1A]">{title}</p>
                    <Badge variant="outline" className={statusClass(agreement.status)}>
                      {documentStatus(agreement)} v{agreement.version || 1}
                    </Badge>
                    {variant && (
                      <Badge variant="outline" className="border-[#D4AF37]/45 bg-[#D4AF37]/12 text-[#7A5A05]">
                        {variant}
                      </Badge>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-[#062B1A]/45">
                    {[
                      agreement.file_name,
                      documentTimestamp(agreement) ? `Updated ${documentTimestamp(agreement)}` : '',
                    ].filter(Boolean).join(' - ')}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    onClick={openEditor}
                    disabled={editorLoading}
                    className="bg-[#062B1A] text-white hover:bg-[#041B10]"
                  >
                    {editorLoading
                      ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      : <Pencil className="mr-2 h-4 w-4" />}
                    Edit
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownload(agreement)}
                    disabled={downloadingId === agreement.id}
                  >
                    {downloadingId === agreement.id
                      ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      : <FileDown className="mr-2 h-4 w-4" />}
                    Download
                  </Button>
                  {history.length > 0 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setHistoryOpen((current) => !current)}
                    >
                      <History className="mr-2 h-4 w-4" />
                      History ({history.length})
                    </Button>
                  )}
                </div>
              </div>

              {historyOpen && history.length > 0 && (
                <div className="mt-4 space-y-2 border-t border-[rgba(6,43,26,0.08)] pt-3">
                  {history.map((document) => (
                    <div
                      key={document.id}
                      className="flex flex-col gap-2 rounded-lg bg-[#F7F8FA] px-3 py-2 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div>
                        <p className="text-sm font-medium text-[#062B1A]">
                          Version {document.version || 1} - {documentStatus(document)}
                        </p>
                        <p className="text-xs text-[#062B1A]/45">
                          {[document.file_name, documentTimestamp(document)].filter(Boolean).join(' - ')}
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownload(document)}
                        disabled={downloadingId === document.id}
                      >
                        {downloadingId === document.id
                          ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          : <FileDown className="mr-2 h-4 w-4" />}
                        Download
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>

      <Dialog open={Boolean(editor)} onOpenChange={(open) => (open ? null : closeEditor())}>
        <DialogContent className="flex h-[94vh] max-w-[calc(100vw-1rem)] flex-col overflow-hidden p-4 sm:max-w-[calc(100vw-3rem)] sm:p-6">
          <DialogHeader>
            <DialogTitle>{editor?.label || title}</DialogTitle>
          </DialogHeader>
          {editorLoading ? (
            <div className="flex flex-1 items-center justify-center gap-2 text-sm text-[#062B1A]/55">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading the current agreement...
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[rgba(6,43,26,0.10)] bg-[#F7F8FA] px-3 py-2">
                <p className="text-xs text-[#062B1A]/60">
                  Click highlighted fields to edit them. Saving creates a new agreement version.
                </p>
                <Button type="button" size="sm" variant="outline" onClick={toggleHighlights}>
                  <Highlighter className="mr-2 h-4 w-4" />
                  Edit highlights: {showHighlights ? 'on' : 'off'}
                </Button>
              </div>
              <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-[rgba(6,43,26,0.14)] bg-[#5c5f66]">
                <iframe
                  key={editor?.documentId}
                  ref={frameRef}
                  title={editor?.label || title}
                  srcDoc={editor?.html || ''}
                  sandbox="allow-same-origin"
                  onLoad={handleFrameLoad}
                  style={{ height: 900 }}
                  className="block w-full border-0"
                />
              </div>
            </>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeEditor} disabled={editorSaving}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={editorLoading || editorSaving}
              className="bg-[#062B1A] text-white hover:bg-[#041B10]"
            >
              {editorSaving
                ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                : <Save className="mr-2 h-4 w-4" />}
              Save new version
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
